import { useState, useEffect, useCallback } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { useTranslation } from "react-i18next"
import { saveTranslations, loadTranslations } from "./translations"

const ReviewsManager = () => {
  const { i18n } = useTranslation("global")
  const [reviews, setReviews] = useState([])
  const [editingId, setEditingId] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)

  // Store full translations for both languages
  const [itTranslations, setItTranslations] = useState(null)
  const [enTranslations, setEnTranslations] = useState(null)

  // Platform stats (scores and review counts)
  const [stats, setStats] = useState({
    googleScore: "",
    googleReviewsIt: "",
    googleReviewsEn: "",
    googleUrl: "",
    tripAdvisorScore: "",
    tripAdvisorReviewsIt: "",
    tripAdvisorReviewsEn: "",
    tripAdvisorUrl: ""
  })

  // Load reviews from both languages
  useEffect(() => {
    const loadReviews = async () => {
      setLoading(true)
      try {
        const [itData, enData] = await Promise.all([
          loadTranslations("it"),
          loadTranslations("en")
        ])

        if (
          !itData || !enData ||
          Array.isArray(itData) || Array.isArray(enData) ||
          typeof itData !== "object" || typeof enData !== "object" ||
          Object.keys(itData).length === 0 || Object.keys(enData).length === 0
        ) {
          throw new Error("Failed to load translations: received empty or invalid data. Try refreshing.")
        }

        setItTranslations(itData)
        setEnTranslations(enData)

        // Get reviews from Italian (primary) - use as base
        const itReviews = itData?.featuredReviews || []
        const enReviews = enData?.featuredReviews || []

        // Merge reviews - shared fields from IT, text from both
        const mergedReviews = itReviews.map((itReview, index) => {
          const enReview = enReviews[index] || {}
          return {
            dragId: `review-${index}`,
            originalId: itReview.id,
            name: itReview.name || "",
            rating: itReview.rating || 5,
            source: itReview.source || "google",
            date: itReview.date || "",
            textIt: itReview.text || "",
            textEn: enReview.text || ""
          }
        })

        setReviews(mergedReviews)

        // Load platform stats
        const rewards = itData?.aboutUs?.rewards || {}
        const rewardsEn = enData?.aboutUs?.rewards || {}
        setStats({
          googleScore: rewards.googleScore || "",
          googleReviewsIt: rewards.googleReviews || "",
          googleReviewsEn: rewardsEn.googleReviews || "",
          googleUrl: rewards.googleUrl || "https://g.page/r/CQPdKqZFxh2rEB0/review",
          tripAdvisorScore: rewards.tripAdvisorScore || "",
          tripAdvisorReviewsIt: rewards.tripAdvisorReviews || "",
          tripAdvisorReviewsEn: rewardsEn.tripAdvisorReviews || "",
          tripAdvisorUrl: rewards.tripAdvisorUrl || "https://www.tripadvisor.it/Restaurant_Review-g790405-d4922962-Reviews-Casa_Dipinta-Spilimbergo_Province_of_Pordenone_Friuli_Venezia_Giulia.html"
        })
      } catch (error) {
        console.error("[ReviewsManager] Error loading:", error)
        setMessage({ type: "error", text: "Failed to load reviews: " + error.message })
      } finally {
        setLoading(false)
      }
    }

    loadReviews()
  }, [])

  // Handle drag end
  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return

    setReviews(prevReviews => {
      const items = Array.from(prevReviews)
      const [reorderedItem] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reorderedItem)

      // Reassign dragIds
      return items.map((item, index) => ({
        ...item,
        dragId: `review-${index}`
      }))
    })
  }, [])

  // Update a review field
  const updateReview = useCallback((dragId, field, value) => {
    setReviews(prevReviews => prevReviews.map(r =>
      r.dragId === dragId
        ? { ...r, [field]: field === "rating" ? parseFloat(value) : value }
        : r
    ))
  }, [])

  // Add new review
  const addReview = useCallback(() => {
    const newDragId = `review-${Date.now()}`
    const newReview = {
      dragId: newDragId,
      originalId: Date.now(),
      name: "New Reviewer",
      textIt: "Testo della recensione...",
      textEn: "Review text here...",
      rating: 5,
      source: "google",
      date: new Date().toISOString().slice(0, 7)
    }
    setReviews(prev => [...prev, newReview])
    setEditingId(newDragId)
  }, [])

  // Delete review
  const deleteReview = useCallback((dragId) => {
    if (window.confirm("Delete this review?")) {
      setReviews(prevReviews => {
        const filtered = prevReviews.filter(r => r.dragId !== dragId)
        return filtered.map((item, index) => ({
          ...item,
          dragId: `review-${index}`
        }))
      })
    }
  }, [])

  // Save to server - CRITICAL FUNCTION
  const handleSave = async (e) => {
    // Prevent any form submission
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (!itTranslations || !enTranslations) {
      setMessage({ type: "error", text: "Translations not loaded. Please reload and try again." })
      return
    }

    setIsSaving(true)
    setMessage({ type: "loading", text: "Saving reviews..." })

    try {
      // Prepare reviews for each language
      const itReviewsToSave = reviews.map(({ originalId, name, rating, source, date, textIt }, index) => ({
        id: originalId || index + 1,
        name,
        rating,
        source,
        date: date || new Date().toISOString().slice(0, 7),
        text: textIt
      }))

      const enReviewsToSave = reviews.map(({ originalId, name, rating, source, date, textEn }, index) => ({
        id: originalId || index + 1,
        name,
        rating,
        source,
        date: date || new Date().toISOString().slice(0, 7),
        text: textEn
      }))

      // Create deep copies of full translations and update featuredReviews
      const updatedIt = JSON.parse(JSON.stringify(itTranslations))
      updatedIt.featuredReviews = itReviewsToSave

      const updatedEn = JSON.parse(JSON.stringify(enTranslations))
      updatedEn.featuredReviews = enReviewsToSave

      // Update platform stats in aboutUs.rewards
      if (!updatedIt.aboutUs) updatedIt.aboutUs = {}
      if (!updatedIt.aboutUs.rewards) updatedIt.aboutUs.rewards = {}
      updatedIt.aboutUs.rewards.googleScore = parseFloat(stats.googleScore) || stats.googleScore
      updatedIt.aboutUs.rewards.googleReviews = stats.googleReviewsIt
      updatedIt.aboutUs.rewards.googleUrl = stats.googleUrl
      updatedIt.aboutUs.rewards.tripAdvisorScore = parseFloat(stats.tripAdvisorScore) || stats.tripAdvisorScore
      updatedIt.aboutUs.rewards.tripAdvisorReviews = stats.tripAdvisorReviewsIt
      updatedIt.aboutUs.rewards.tripAdvisorUrl = stats.tripAdvisorUrl

      if (!updatedEn.aboutUs) updatedEn.aboutUs = {}
      if (!updatedEn.aboutUs.rewards) updatedEn.aboutUs.rewards = {}
      updatedEn.aboutUs.rewards.googleScore = parseFloat(stats.googleScore) || stats.googleScore
      updatedEn.aboutUs.rewards.googleReviews = stats.googleReviewsEn
      updatedEn.aboutUs.rewards.googleUrl = stats.googleUrl
      updatedEn.aboutUs.rewards.tripAdvisorScore = parseFloat(stats.tripAdvisorScore) || stats.tripAdvisorScore
      updatedEn.aboutUs.rewards.tripAdvisorReviews = stats.tripAdvisorReviewsEn
      updatedEn.aboutUs.rewards.tripAdvisorUrl = stats.tripAdvisorUrl

      await saveTranslations("it", updatedIt)
      await saveTranslations("en", updatedEn)

      // Update i18n resources
      i18n.addResourceBundle("it", "global", updatedIt, true, true)
      i18n.addResourceBundle("en", "global", updatedEn, true, true)

      // Update local state
      setItTranslations(updatedIt)
      setEnTranslations(updatedEn)

      setMessage({ type: "success", text: "Reviews saved successfully! Reloading..." })

      // Reload page after delay
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } catch (error) {
      console.error("[ReviewsManager] Save error:", error)
      setMessage({ type: "error", text: "Failed to save: " + error.message })
      setIsSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading reviews...</div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto" style={{ maxHeight: "calc(100vh - 100px)" }}>
      <div className="p-3 sm:p-6">
        {/* Header - stacks on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-heading text-secondary-200">Reviews Manager</h1>
            <p className="text-gray-600 text-xs sm:text-sm">Drag to reorder. Name, rating, source are shared. Text is per language.</p>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={`w-full sm:w-auto px-6 py-2 rounded-lg font-medium transition-colors ${
              isSaving
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-secondary-200 text-white hover:bg-secondary-400"
            }`}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-4 p-3 rounded-lg ${
            message.type === "success" ? "bg-green-100 text-green-700" :
            message.type === "error" ? "bg-red-100 text-red-700" :
            "bg-blue-100 text-blue-700"
          }`}>
            {message.text}
          </div>
        )}

        {/* Platform Stats Section */}
        <div className="bg-white rounded-lg border-2 border-gray-200 p-3 sm:p-4 mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-secondary-200 mb-3 sm:mb-4">Platform Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Google Stats */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">G</span>
                </div>
                <span className="font-medium text-gray-700">Google</span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Score (shared)</label>
                <input
                  type="text"
                  value={stats.googleScore}
                  onChange={(e) => setStats(prev => ({ ...prev, googleScore: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent"
                  placeholder="4.6"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-4 h-3 rounded bg-green-600 text-white text-[8px] flex items-center justify-center font-bold">IT</span>
                      Reviews Text
                    </span>
                  </label>
                  <input
                    type="text"
                    value={stats.googleReviewsIt}
                    onChange={(e) => setStats(prev => ({ ...prev, googleReviewsIt: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                    placeholder="254 recensioni"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-4 h-3 rounded bg-blue-600 text-white text-[8px] flex items-center justify-center font-bold">EN</span>
                      Reviews Text
                    </span>
                  </label>
                  <input
                    type="text"
                    value={stats.googleReviewsEn}
                    onChange={(e) => setStats(prev => ({ ...prev, googleReviewsEn: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                    placeholder="254 reviews"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Review Page URL (shared)</label>
                <input
                  type="url"
                  value={stats.googleUrl}
                  onChange={(e) => setStats(prev => ({ ...prev, googleUrl: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                  placeholder="https://g.page/r/..."
                />
              </div>
            </div>

            {/* TripAdvisor Stats */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">TA</span>
                </div>
                <span className="font-medium text-gray-700">TripAdvisor</span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Score (shared)</label>
                <input
                  type="text"
                  value={stats.tripAdvisorScore}
                  onChange={(e) => setStats(prev => ({ ...prev, tripAdvisorScore: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent"
                  placeholder="4.6"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-4 h-3 rounded bg-green-600 text-white text-[8px] flex items-center justify-center font-bold">IT</span>
                      Reviews Text
                    </span>
                  </label>
                  <input
                    type="text"
                    value={stats.tripAdvisorReviewsIt}
                    onChange={(e) => setStats(prev => ({ ...prev, tripAdvisorReviewsIt: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                    placeholder="213 recensioni"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-4 h-3 rounded bg-blue-600 text-white text-[8px] flex items-center justify-center font-bold">EN</span>
                      Reviews Text
                    </span>
                  </label>
                  <input
                    type="text"
                    value={stats.tripAdvisorReviewsEn}
                    onChange={(e) => setStats(prev => ({ ...prev, tripAdvisorReviewsEn: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                    placeholder="213 reviews"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Review Page URL (shared)</label>
                <input
                  type="url"
                  value={stats.tripAdvisorUrl}
                  onChange={(e) => setStats(prev => ({ ...prev, tripAdvisorUrl: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                  placeholder="https://www.tripadvisor.it/..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        <h2 className="text-lg font-semibold text-secondary-200 mb-3">Featured Reviews</h2>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="reviews-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-3"
              >
                {reviews.map((review, index) => (
                  <Draggable key={review.dragId} draggableId={review.dragId} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`bg-white rounded-lg border-2 transition-all ${
                          snapshot.isDragging ? "border-secondary-400 shadow-lg" : "border-gray-200"
                        } ${editingId === review.dragId ? "ring-2 ring-secondary-400" : ""}`}
                      >
                        <div className="flex items-start gap-3 p-4">
                          {/* Drag Handle */}
                          <div
                            {...provided.dragHandleProps}
                            className="mt-2 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                            </svg>
                          </div>

                          {/* Review Content */}
                          <div className="flex-1">
                            {editingId === review.dragId ? (
                              /* Edit Mode */
                              <div className="space-y-3 sm:space-y-4">
                                {/* Shared Fields Row - stacks on mobile */}
                                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
                                  <div className="col-span-2 sm:flex-1 sm:min-w-[200px]">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Name (shared)</label>
                                    <input
                                      type="text"
                                      value={review.name}
                                      onChange={(e) => updateReview(review.dragId, "name", e.target.value)}
                                      className="w-full px-2 sm:px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                                      placeholder="Reviewer name"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Source</label>
                                    <select
                                      value={review.source}
                                      onChange={(e) => updateReview(review.dragId, "source", e.target.value)}
                                      className="w-full px-2 sm:px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 text-sm"
                                    >
                                      <option value="google">Google</option>
                                      <option value="tripadvisor">TripAdvisor</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Rating</label>
                                    <select
                                      value={review.rating}
                                      onChange={(e) => updateReview(review.dragId, "rating", e.target.value)}
                                      className="w-full px-2 sm:px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 text-sm"
                                    >
                                      <option value="5">5</option>
                                      <option value="4.5">4.5</option>
                                      <option value="4">4</option>
                                      <option value="3.5">3.5</option>
                                      <option value="3">3</option>
                                    </select>
                                  </div>
                                </div>

                                {/* Text Fields - Side by Side */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      <span className="inline-flex items-center gap-1">
                                        <span className="w-5 h-4 rounded bg-green-600 text-white text-[10px] flex items-center justify-center font-bold">IT</span>
                                        Text (Italian)
                                      </span>
                                    </label>
                                    <textarea
                                      value={review.textIt}
                                      onChange={(e) => updateReview(review.dragId, "textIt", e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent resize-none"
                                      rows={3}
                                      placeholder="Testo della recensione..."
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      <span className="inline-flex items-center gap-1">
                                        <span className="w-5 h-4 rounded bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">EN</span>
                                        Text (English)
                                      </span>
                                    </label>
                                    <textarea
                                      value={review.textEn}
                                      onChange={(e) => updateReview(review.dragId, "textEn", e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent resize-none"
                                      rows={3}
                                      placeholder="Review text..."
                                    />
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => setEditingId(null)}
                                  className="px-4 py-2 bg-secondary-200 text-white rounded-lg hover:bg-secondary-400 transition-colors"
                                >
                                  Done Editing
                                </button>
                              </div>
                            ) : (
                              /* View Mode */
                              <div onClick={() => setEditingId(review.dragId)} className="cursor-pointer">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                    review.source === "google"
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-green-100 text-green-700"
                                  }`}>
                                    {review.source === "google" ? "Google" : "TripAdvisor"}
                                  </span>
                                  <span className="font-semibold text-secondary-200">{review.name}</span>
                                  <span className="text-yellow-500">{"★".repeat(Math.floor(review.rating))}{review.rating % 1 ? "½" : ""}</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                  <div className="flex gap-2">
                                    <span className="w-5 h-4 rounded bg-green-600 text-white text-[10px] flex items-center justify-center font-bold shrink-0">IT</span>
                                    <p className="text-gray-600 line-clamp-2">{review.textIt}</p>
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="w-5 h-4 rounded bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold shrink-0">EN</span>
                                    <p className="text-gray-600 line-clamp-2">{review.textEn}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={() => deleteReview(review.dragId)}
                            className="text-red-400 hover:text-red-600 transition-colors p-1"
                            title="Delete review"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* Add Review Button */}
        <button
          type="button"
          onClick={addReview}
          className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-secondary-400 hover:text-secondary-400 transition-colors"
        >
          + Add New Review
        </button>

        {/* Instructions */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="font-semibold text-amber-800 mb-2">How to use:</h3>
          <ol className="text-amber-700 text-sm space-y-1 list-decimal list-inside">
            <li>Update Google/TripAdvisor scores and review counts at the top</li>
            <li>Click on a review to edit its content</li>
            <li>Name, source, rating, and scores are shared across both languages</li>
            <li>Text fields (review text, reviews count) are separate for IT and EN</li>
            <li>Drag reviews to reorder them</li>
            <li>Click "Save Changes" to update both languages on the server</li>
          </ol>
        </div>
      </div>
    </div>
  )
}

export default ReviewsManager
